import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { useBrandingStore } from '@/store/brandingStore'

// Lazy load all page components for code splitting
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/RegisterPage'))
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'))
const ResendVerificationPage = lazy(() => import('@/pages/ResendVerificationPage'))
const OAuthCallbackPage = lazy(() => import('@/pages/OAuthCallbackPage'))
const HomePage = lazy(() => import('@/pages/HomePage'))
const ComingSoonPage = lazy(() => import('@/pages/ComingSoonPage'))

// Accounting module pages (double-entry bookkeeping)
const DashboardPage = lazy(() => import('@/pages/accounting/DashboardPage'))
const OperationsPage = lazy(() => import('@/pages/accounting/OperationsPage'))
const ReportsPage = lazy(() => import('@/pages/accounting/ReportsPage'))
const CategoriesPage = lazy(() => import('@/pages/accounting/CategoriesPage'))
const BalancesPage = lazy(() => import('@/pages/accounting/BalancesPage'))

// Treasury module pages (cajas, bancos, billeteras virtuales)
const TreasuryDashboardPage = lazy(() => import('@/pages/treasury/TreasuryDashboardPage'))
const TreasuryCashMovementsPage = lazy(() => import('@/pages/treasury/CashMovementsPage'))
const BankAccountsPage = lazy(() => import('@/pages/treasury/BankAccountsPage'))
const WalletsPage = lazy(() => import('@/pages/treasury/WalletsPage'))
const InvestmentsPage = lazy(() => import('@/pages/treasury/InvestmentsPage'))
const OnlinePaymentsPage = lazy(() => import('@/pages/treasury/OnlinePaymentsPage'))
const CierreMercadoPagoPage = lazy(() => import('@/pages/treasury/CierreMercadoPagoPage'))
const PagoPublicoPage = lazy(() => import('@/pages/PagoPublicoPage'))
const SocioLoginPage = lazy(() => import('@/pages/socio/SocioLoginPage'))
const SocioContrasenaPage = lazy(() => import('@/pages/socio/SocioContrasenaPage'))
const SocioNuevaContrasenaPage = lazy(() => import('@/pages/socio/SocioNuevaContrasenaPage'))
const SocioPortalLayout = lazy(() => import('@/pages/socio/SocioPortalLayout'))
const SocioInicioPage = lazy(() => import('@/pages/socio/SocioInicioPage'))
const SocioCuotasPage = lazy(() => import('@/pages/socio/SocioCuotasPage'))
const SocioPrestamosPage = lazy(() => import('@/pages/socio/SocioPrestamosPage'))
const SocioCatalogoPage = lazy(() => import('@/pages/socio/SocioCatalogoPage'))
const SocioFichaPage = lazy(() => import('@/pages/socio/SocioFichaPage'))
const SocioEncargosPage = lazy(() => import('@/pages/socio/SocioEncargosPage'))
const EncargosPage = lazy(() => import('@/pages/library/EncargosPage'))
const PaymentSettingsPage = lazy(() => import('@/pages/admin/PaymentSettingsPage'))
const SettlementsPage = lazy(() => import('@/pages/treasury/SettlementsPage'))
const SubdiarioReportPage = lazy(() => import('@/pages/treasury/SubdiarioReportPage'))
const SubdiarioCajaPage = lazy(() => import('@/pages/treasury/SubdiarioCajaPage'))
const TreasurySubmayoresPage = lazy(() => import('@/pages/treasury/SubmayoresPage'))

// Purchases module pages
const PurchasesDashboardPage = lazy(() => import('@/pages/purchases/PurchasesDashboardPage'))
const SuppliersPage = lazy(() => import('@/pages/purchases/SuppliersPage'))
const RequestsPage = lazy(() => import('@/pages/purchases/RequestsPage'))
const RequestDetailPage = lazy(() => import('@/pages/purchases/RequestDetailPage'))
const NewRequestPage = lazy(() => import('@/pages/purchases/NewRequestPage'))
const QuotationsPage = lazy(() => import('@/pages/purchases/QuotationsPage'))
const QuotationComparePage = lazy(() => import('@/pages/purchases/QuotationComparePage'))
const OrderDetailPage = lazy(() => import('@/pages/purchases/OrderDetailPage'))
const PurchasesSettingsPage = lazy(() => import('@/pages/purchases/SettingsPage'))

// Rentals module
const RentalsDashboardPage = lazy(() => import('@/pages/rentals/RentalsDashboardPage'))
const RentalsSpacesPage = lazy(() => import('@/pages/rentals/SpacesPage'))
const RentalsBookingsPage = lazy(() => import('@/pages/rentals/BookingsPage'))
const RentalsSeriesPage = lazy(() => import('@/pages/rentals/SeriesPage'))

// Socios module pages
const SociosPage = lazy(() => import('@/pages/SociosPage'))
const SociosReportsPage = lazy(() => import('@/pages/socios/ReportsPage'))
const BajasPage = lazy(() => import('@/pages/socios/BajasPage'))

// Admin module pages
const AdminDashboardPage = lazy(() => import('@/pages/admin/DashboardPage'))
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'))
const SystemPage = lazy(() => import('@/pages/admin/SystemPage'))
const RolesPage = lazy(() => import('@/pages/admin/RolesPage'))
const RolePermissionsPage = lazy(() => import('@/pages/admin/RolePermissionsPage'))
const SettingsPage = lazy(() => import('@/pages/admin/SettingsPage'))
const EmailSettingsPage = lazy(() => import('@/pages/admin/EmailSettingsPage'))
const WhatsAppSettingsPage = lazy(() => import('@/pages/admin/WhatsAppSettingsPage'))
const ChatbotSettingsPage = lazy(() => import('@/pages/admin/ChatbotSettingsPage'))
const OAuthSettingsPage = lazy(() => import('@/pages/admin/OAuthSettingsPage'))
const AuditPage = lazy(() => import('@/pages/admin/AuditPage'))
const AlertSettingsPage = lazy(() => import('@/pages/admin/AlertSettingsPage'))
const BackupSettingsPage = lazy(() => import('@/pages/admin/BackupSettingsPage'))

// Profile and auth pages
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const PendingApprovalPage = lazy(() => import('@/pages/PendingApprovalPage'))

// Module layouts
import { SociosLayout } from '@/components/layouts/SociosLayout'
import { LibraryLayout } from '@/components/layouts/LibraryLayout'
import { AccountingLayout } from '@/components/layouts/AccountingLayout'
import { TreasuryLayout } from '@/components/layouts/TreasuryLayout'
import { PurchasesLayout } from '@/components/layouts/PurchasesLayout'
import { RentalsLayout } from '@/components/layouts/RentalsLayout'
import { AdminModuleLayout } from '@/components/layouts/AdminModuleLayout'

// Loading component for Suspense fallback
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-foreground"></div>
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    </div>
  )
}

function App() {
  const loadBranding = useBrandingStore((state) => state.loadBranding)

  useEffect(() => {
    loadBranding()
  }, [loadBranding])

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} errorElement={<RouteErrorBoundary />} />
          <Route path="/register" element={<RegisterPage />} errorElement={<RouteErrorBoundary />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} errorElement={<RouteErrorBoundary />} />
          <Route path="/resend-verification" element={<ResendVerificationPage />} errorElement={<RouteErrorBoundary />} />
          <Route path="/oauth-callback" element={<OAuthCallbackPage />} errorElement={<RouteErrorBoundary />} />

          {/* Pago público de cuotas: sin login, lo identifica el token del link */}
          <Route path="/pagar/:token" element={<PagoPublicoPage />} errorElement={<RouteErrorBoundary />} />
          <Route path="/pagar/:token/resultado" element={<PagoPublicoPage />} errorElement={<RouteErrorBoundary />} />

          {/* Portal del socio: autenticación propia (socio_token), separada de la de
              empleados. Las rutas son públicas para el router; el backend exige el token. */}
          <Route path="/socio/login" element={<SocioLoginPage />} errorElement={<RouteErrorBoundary />} />
          <Route path="/socio/contrasena" element={<SocioContrasenaPage />} errorElement={<RouteErrorBoundary />} />
          <Route path="/socio/contrasena/:token" element={<SocioNuevaContrasenaPage />} errorElement={<RouteErrorBoundary />} />
          {/* El alta pasó a ser el mismo circuito que cambiar la contraseña */}
          <Route path="/socio/registro" element={<Navigate to="/socio/contrasena" replace />} />
          <Route path="/socio" element={<SocioPortalLayout />} errorElement={<RouteErrorBoundary />}>
            <Route index element={<SocioInicioPage />} />
            <Route path="cuotas" element={<SocioCuotasPage />} />
            <Route path="prestamos" element={<SocioPrestamosPage />} />
            <Route path="catalogo" element={<SocioCatalogoPage />} />
            <Route path="catalogo/:id" element={<SocioFichaPage />} />
            <Route path="encargos" element={<SocioEncargosPage />} />
          </Route>

          {/* Protected routes - auth status */}
          <Route
            path="/pending-approval"
            element={
              <ProtectedRoute>
                <PendingApprovalPage />
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute requireApprovedUser>
                <ProfilePage />
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          {/* Home/Dashboard - Show module selection page for authenticated users */}
          <Route
            path="/"
            element={
              <ProtectedRoute requireApprovedUser>
                <HomePage />
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          {/* Legacy dashboard redirect */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />

          {/* Socios Module */}
          <Route
            path="/socios"
            element={
              <ProtectedRoute resource="library_associateds">
                <SociosLayout>
                  <SociosPage />
                </SociosLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/socios/reports"
            element={
              <ProtectedRoute resource="reports">
                <SociosLayout>
                  <SociosReportsPage />
                </SociosLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/socios/bajas"
            element={
              <ProtectedRoute resource="library_bajas">
                <SociosLayout>
                  <BajasPage />
                </SociosLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          <Route
            path="/library/encargos"
            element={
              <ProtectedRoute resource="library_encargos">
                <LibraryLayout>
                  <EncargosPage />
                </LibraryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          {/* Library Module - Placeholder pages */}
          <Route
            path="/library/*"
            element={
              <ProtectedRoute resource="library_associateds">
                <LibraryLayout>
                  <ComingSoonPage
                    title="Módulo de Biblioteca"
                    description="El módulo de biblioteca está en desarrollo. Pronto podrás gestionar el catálogo de libros y préstamos."
                    showHomeButton={false}
                  />
                </LibraryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          {/* Accounting Module */}
          <Route
            path="/accounting"
            element={
              <ProtectedRoute resource="accounting">
                <AccountingLayout>
                  <Navigate to="/accounting/dashboard" replace />
                </AccountingLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/accounting/dashboard"
            element={
              <ProtectedRoute resource="accounting">
                <AccountingLayout>
                  <DashboardPage />
                </AccountingLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/accounting/operations"
            element={
              <ProtectedRoute resource="accounting">
                <AccountingLayout>
                  <OperationsPage />
                </AccountingLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/accounting/accounts"
            element={<Navigate to="/accounting/categories" replace />}
          />
          <Route
            path="/accounting/reports"
            element={
              <ProtectedRoute resource="accounting">
                <AccountingLayout>
                  <ReportsPage />
                </AccountingLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/accounting/categories"
            element={
              <ProtectedRoute resource="accounting">
                <AccountingLayout>
                  <CategoriesPage />
                </AccountingLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/accounting/balances"
            element={
              <ProtectedRoute resource="accounting">
                <AccountingLayout>
                  <BalancesPage />
                </AccountingLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          {/* Legacy accounting routes - redirect to new locations */}
          <Route path="/accounting/cash-movements" element={<Navigate to="/treasury/cash-movements" replace />} />
          <Route path="/accounting/subdiario" element={<Navigate to="/treasury/cash-movements" replace />} />
          <Route path="/accounting/reconciliations" element={<Navigate to="/treasury/cash-movements" replace />} />
          <Route path="/accounting/expenses" element={<Navigate to="/accounting/operations" replace />} />
          <Route path="/accounting/incomes" element={<Navigate to="/accounting/operations" replace />} />
          <Route path="/accounting/transfers" element={<Navigate to="/accounting/operations" replace />} />
          <Route path="/accounting/transfer-types" element={<Navigate to="/accounting/categories" replace />} />

          {/* Treasury Module */}
          <Route
            path="/treasury"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <Navigate to="/treasury/dashboard" replace />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/dashboard"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <TreasuryDashboardPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/cash-movements"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <TreasuryCashMovementsPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/bank-accounts"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <BankAccountsPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/wallets"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <WalletsPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/investments"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <InvestmentsPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/online-payments"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <OnlinePaymentsPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/cierre-mercadopago"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <CierreMercadoPagoPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/settlements"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <SettlementsPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/subdiario"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <SubdiarioReportPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/submayores"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <TreasurySubmayoresPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/treasury/pase-diario"
            element={
              <ProtectedRoute resource="treasury">
                <TreasuryLayout>
                  <SubdiarioCajaPage />
                </TreasuryLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          {/* Purchases Module */}
          <Route
            path="/purchases"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <PurchasesDashboardPage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/purchases/suppliers"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <SuppliersPage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/purchases/requests"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <RequestsPage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/purchases/requests/new"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <NewRequestPage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/purchases/requests/:id/edit"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <NewRequestPage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/purchases/requests/:id"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <RequestDetailPage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/purchases/quotations"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <QuotationsPage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/purchases/quotations/compare/:requestId"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <QuotationComparePage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/purchases/orders/:id"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <OrderDetailPage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/purchases/settings"
            element={
              <ProtectedRoute resource="purchase_requests">
                <PurchasesLayout>
                  <PurchasesSettingsPage />
                </PurchasesLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          {/* Rentals Module */}
          <Route
            path="/rentals"
            element={
              <ProtectedRoute resource="rentals">
                <RentalsLayout>
                  <Navigate to="/rentals/dashboard" replace />
                </RentalsLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/rentals/dashboard"
            element={
              <ProtectedRoute resource="rentals">
                <RentalsLayout>
                  <RentalsDashboardPage />
                </RentalsLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/rentals/spaces"
            element={
              <ProtectedRoute resource="rentals">
                <RentalsLayout>
                  <RentalsSpacesPage />
                </RentalsLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/rentals/bookings"
            element={
              <ProtectedRoute resource="rentals">
                <RentalsLayout>
                  <RentalsBookingsPage />
                </RentalsLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/rentals/series"
            element={
              <ProtectedRoute resource="rentals">
                <RentalsLayout>
                  <RentalsSeriesPage />
                </RentalsLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          {/* Admin Module */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <Navigate to="/admin/dashboard" replace />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <AdminDashboardPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <UsersPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <AuditPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/system"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <SystemPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <RolesPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/roles/:roleId/permissions"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <RolePermissionsPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <SettingsPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/email-settings"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <EmailSettingsPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/alert-settings"
            element={
              <ProtectedRoute resource="settings">
                <AdminModuleLayout>
                  <AlertSettingsPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/whatsapp-settings"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <WhatsAppSettingsPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/chatbot-settings"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <ChatbotSettingsPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />
          <Route
            path="/admin/oauth-settings"
            element={
              <ProtectedRoute resource="users">
                <AdminModuleLayout>
                  <OAuthSettingsPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          <Route
            path="/admin/payment-settings"
            element={
              <ProtectedRoute resource="settings">
                <AdminModuleLayout>
                  <PaymentSettingsPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          <Route
            path="/admin/backup-settings"
            element={
              <ProtectedRoute resource="settings">
                <AdminModuleLayout>
                  <BackupSettingsPage />
                </AdminModuleLayout>
              </ProtectedRoute>
            }
            errorElement={<RouteErrorBoundary />}
          />

          {/* 404 and fallback */}
          <Route
            path="*"
            element={
              <ComingSoonPage
                title="Página no encontrada"
                description="La página que buscas no existe o aún no está disponible."
              />
            }
          />
        </Routes>
        <Toaster />
      </Suspense>
    </ErrorBoundary>
  )
}

export default App
